/**
 * useJobOffers — reactive state wrapper over the job-offers-store.
 *
 * Exposes CRUD operations and keeps local state in sync so React re-renders
 * on changes (localStorage is not reactive by default).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  getJobOffers,
  addJobOffer,
  updateJobOffer,
  deleteJobOffer,
  toggleJobOfferFavorite,
  getCVxJobEntries,
  upsertCVxJob,
  getBestScoreForCv,
  getBestScoreForJob,
  getApplications,
  upsertApplication,
  removeApplication,
  type JobOffer,
  type CVxJobEntry,
  type ApplicationTracking,
  type ApplicationStatus,
} from '../lib/job-offers-store';
import type { ParsedJobOffer } from '../types';

export interface UseJobOffersReturn {
  jobOffers: JobOffer[];
  cvxjobEntries: CVxJobEntry[];
  applications: ApplicationTracking[];
  addOffer: (offer: Omit<JobOffer, 'id' | 'dateAdded'>) => JobOffer;
  updateOffer: (id: string, updates: Partial<JobOffer>) => void;
  removeOffer: (id: string) => void;
  toggleFavorite: (id: string) => void;
  recordAnalysis: (cvVersionId: string, jobOfferId: string, atsScore: number) => void;
  recordAdaptation: (cvVersionId: string, jobOfferId: string, adaptedCvId: string) => void;
  setApplicationStatus: (jobOfferId: string, status: ApplicationStatus) => void;
  setApplicationNotes: (jobOfferId: string, notes: string) => void;
  clearApplication: (jobOfferId: string) => void;
  getBestScoreForCv: (cvVersionId: string) => number | null;
  getBestScoreForJob: (jobOfferId: string) => { score: number; cvVersionId: string } | null;
  refresh: () => void;
}

export function useJobOffers(): UseJobOffersReturn {
  const [jobOffers, setJobOffers] = useState<JobOffer[]>(() => getJobOffers());
  const [cvxjobEntries, setCvxjobEntries] = useState<CVxJobEntry[]>(() => getCVxJobEntries());
  const [applications, setApplications] = useState<ApplicationTracking[]>(() => getApplications());

  // Cross-tab sync: when another tab modifies localStorage, refresh our state
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'cv_job_offers') setJobOffers(getJobOffers());
      if (e.key === 'cv_xjob_entries') setCvxjobEntries(getCVxJobEntries());
      if (e.key === 'cv_applications') setApplications(getApplications());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Sync offers index to extension storage (for banner "already saved" status)
  useEffect(() => {
    const index = jobOffers.map((o) => ({ id: o.id, title: o.title, company: o.company }));
    window.postMessage({ type: 'ATSmyCV_OFFERS_SYNC', index }, '*');
  }, [jobOffers]);

  const refresh = useCallback(() => {
    setJobOffers(getJobOffers());
    setCvxjobEntries(getCVxJobEntries());
    setApplications(getApplications());
  }, []);

  const addOffer = useCallback((offer: Omit<JobOffer, 'id' | 'dateAdded'>): JobOffer => {
    const created = addJobOffer(offer);
    setJobOffers(getJobOffers());
    return created;
  }, []);

  const updateOffer = useCallback((id: string, updates: Partial<JobOffer>) => {
    updateJobOffer(id, updates);
    setJobOffers(getJobOffers());
  }, []);

  const removeOffer = useCallback((id: string) => {
    deleteJobOffer(id);
    setJobOffers(getJobOffers());
    setCvxjobEntries(getCVxJobEntries());
  }, []);

  const toggleFavorite = useCallback((id: string) => {
    toggleJobOfferFavorite(id);
    setJobOffers(getJobOffers());
  }, []);

  const recordAnalysis = useCallback((cvVersionId: string, jobOfferId: string, atsScore: number) => {
    upsertCVxJob(cvVersionId, jobOfferId, { atsScore });
    setCvxjobEntries(getCVxJobEntries());
  }, []);

  const recordAdaptation = useCallback((cvVersionId: string, jobOfferId: string, adaptedCvId: string) => {
    upsertCVxJob(cvVersionId, jobOfferId, { adaptedCvId });
    setCvxjobEntries(getCVxJobEntries());
  }, []);

  const setApplicationStatus = useCallback((jobOfferId: string, status: ApplicationStatus) => {
    upsertApplication(jobOfferId, { status });
    setApplications(getApplications());
  }, []);

  const setApplicationNotes = useCallback((jobOfferId: string, notes: string) => {
    upsertApplication(jobOfferId, { notes });
    setApplications(getApplications());
  }, []);

  const clearApplication = useCallback((jobOfferId: string) => {
    removeApplication(jobOfferId);
    setApplications(getApplications());
  }, []);

  return {
    jobOffers,
    cvxjobEntries,
    applications,
    addOffer,
    updateOffer,
    removeOffer,
    toggleFavorite,
    recordAnalysis,
    recordAdaptation,
    setApplicationStatus,
    setApplicationNotes,
    clearApplication,
    getBestScoreForCv,
    getBestScoreForJob,
    refresh,
  };
}

export type { JobOffer, CVxJobEntry, ParsedJobOffer, ApplicationTracking, ApplicationStatus };
